import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const STORAGE_KEY = 'bha_auth';

// Decode a JWT and check if it's expired (or within 30s of expiring).
function isTokenExpired(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp < (Date.now() / 1000) + 30;
  } catch {
    return true;
  }
}

// account_type values from backend:
//   'guest' | 'comp_individual' | 'comp_org' |
//   'subscriber_individual' | 'subscriber_org' | 'admin'

const FULL_ACCESS_TYPES = new Set([
  'comp_individual',
  'comp_org',
  'subscriber_individual',
  'subscriber_org',
  'admin',
]);

function applyAxiosToken(token) {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
}

export function AuthProvider({ children }) {
  const [user, setUser]   = useState(null);
  const [token, setToken] = useState(null);
  const [ready, setReady] = useState(false);

  // Use refs so the interceptor always has the latest tokens
  const refreshTokenRef   = useRef(null);
  const sessionIdRef      = useRef(null);
  // Single in-flight refresh shared by the interceptor and the mount-time
  // restore, so a rotated (single-use) refresh token is never spent twice.
  const refreshPromiseRef = useRef(null);

  const clearSession = () => {
    setUser(null);
    setToken(null);
    refreshTokenRef.current = null;
    sessionIdRef.current    = null;
    applyAxiosToken(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  const performRefresh = () => {
    if (refreshPromiseRef.current) return refreshPromiseRef.current;

    const refreshToken = refreshTokenRef.current;
    if (!refreshToken) return Promise.reject(new Error('No refresh token'));

    // _skipAuthRefresh keeps the interceptor from recursing on this request's own 401.
    refreshPromiseRef.current = axios
      .post(
        '/api/auth/refresh',
        { refresh_token: refreshToken, session_id: sessionIdRef.current },
        { _skipAuthRefresh: true }
      )
      .then(({ data }) => {
        refreshTokenRef.current = data.refresh_token;
        setToken(data.token);
        applyAxiosToken(data.token);
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            localStorage.setItem(STORAGE_KEY, JSON.stringify({
              ...parsed,
              token:         data.token,
              refresh_token: data.refresh_token,
            }));
          } catch { /* ignore */ }
        }
        return data.token;
      })
      .catch((err) => {
        // Server rejected the refresh token → session is gone. Network errors leave it alone.
        if (err.response && (err.response.status === 401 || err.response.status === 400)) {
          clearSession();
        }
        throw err;
      })
      .finally(() => {
        refreshPromiseRef.current = null;
      });

    return refreshPromiseRef.current;
  };

  const saveSession = (userData, accessToken, refreshToken, sessionId = null) => {
    setUser(userData);
    setToken(accessToken);
    refreshTokenRef.current = refreshToken;
    sessionIdRef.current    = sessionId;
    applyAxiosToken(accessToken);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      user:          userData,
      token:         accessToken,
      refresh_token: refreshToken,
      session_id:    sessionId,
    }));
  };

  // Restore session from localStorage on mount.
  // Restoration is synchronous so pages render immediately (no blank screen).
  // If the token is expired, a background refresh runs concurrently — the
  // axios interceptor also handles 401s as a fallback.
  useEffect(() => {
    const restore = async () => {
      let restoredRefresh = null;

      // ── Step 1: synchronous restore ──────────────────────────────
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const {
            user: savedUser,
            token: savedToken,
            refresh_token: savedRefresh,
            session_id: savedSessionId,
          } = JSON.parse(raw);

          if (savedUser && savedToken) {
            setUser(savedUser);
            setToken(savedToken);
            refreshTokenRef.current = savedRefresh   || null;
            sessionIdRef.current    = savedSessionId || null;
            applyAxiosToken(savedToken);
            restoredRefresh   = savedRefresh   || null;
          }
        }
      } catch {
        localStorage.removeItem(STORAGE_KEY);
      } finally {
        // Always set ready immediately so pages can render.
        setReady(true);
      }

      // ── Step 2: background token refresh (non-blocking) ──────────
      // If the stored token is expired and we have a refresh token, silently
      // refresh it now. The axios interceptor handles 401s as a fallback.
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && restoredRefresh) {
        try {
          const { token: storedToken } = JSON.parse(stored);
          if (storedToken && isTokenExpired(storedToken)) {
            await performRefresh();
          }
        } catch {
          // performRefresh already cleared the session if the server rejected the token.
        }
      }
    };

    restore();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Axios interceptor — automatically refresh token on 401
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config;

        const shouldRefresh =
          error.response?.status === 401 &&
          original &&
          !original._retry &&
          !original._skipAuthRefresh &&
          refreshTokenRef.current;

        if (!shouldRefresh) return Promise.reject(error);

        original._retry = true;
        try {
          const newToken = await performRefresh();
          original.headers['Authorization'] = `Bearer ${newToken}`;
          return axios(original);
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Called after successful login or signup
  const login = (userData, accessToken, refreshToken, sessionId = null) => {
    saveSession(userData, accessToken, refreshToken, sessionId);
  };

  // Called on logout
  const logout = () => {
    // Fire-and-forget: remove session record from DB so it doesn't count toward org limit
    if (sessionIdRef.current) {
      axios.post('/api/auth/logout', { session_id: sessionIdRef.current }).catch(() => {});
    }
    clearSession();
  };

  // Sync updated user data without touching tokens
  const updateUser = (userData) => {
    setUser(userData);
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...parsed, user: userData }));
      } catch { /* ignore */ }
    }
  };

  const isAuthenticated = !!user;
  const isAdmin         = user?.account_type === 'admin';
  const hasFullAccess   = !!user && FULL_ACCESS_TYPES.has(user.account_type);
  const isOrgAccount    = user?.account_type === 'comp_org' || user?.account_type === 'subscriber_org';

  const value = {
    user,
    token,
    ready,
    login,
    logout,
    updateUser,
    isAuthenticated,
    isAdmin,
    hasFullAccess,
    isOrgAccount,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
